/**
 * ScriptSureError.tsx
 *
 * What the patient Prescriptions tab and the Prescribing page say when a
 * ScriptSure window cannot open. Both surfaces map the server's error codes
 * through errorViewFor and render ScriptSureErrorNotice, so each code reads the
 * same wherever it lands.
 */
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/services/apiClient';
import { getDawErrorCode, getDawMissingFields } from '@/services/prescriptions.service';

export const NOT_CONFIGURED_TEXT =
  'Prescribing has not been set up for your practice yet. Contact support to turn it on.';
// Covers every reason ScriptSure rejects the user login — no account, an
// inactive one, one past its end date, or one not attached to our application.
// They are one problem to the reader and one fix for the administrator, and we
// cannot tell them apart (see DawPrescribingService::freshLogin), so the copy
// must cover the whole space without guessing which applies.
export const UNKNOWN_USER_TEXT =
  'Your prescribing access is not set up yet, so this cannot open. Ask your administrator to give you access in ScriptSure using this same email address, and to check that your account there is active.';
// Covers both halves of DAW_PATIENT_INCOMPLETE: a field that is absent, and one
// that is present but longer than the cap SureScripts enforces. "Missing" alone
// read wrong for the second case.
export const PATIENT_INCOMPLETE_TEXT =
  "This patient's details need fixing before you can prescribe for them:";
export const GENERIC_FAILURE_TEXT =
  'Prescribing is unavailable right now. Try again in a moment, and contact support if it keeps happening.';

// Failures a retry cannot fix: account config, ScriptSure user identity,
// patient demographics, and the module being switched off. Their error views
// stay button-less; everything else gets a "Try again".
const RETRY_EXEMPT_CODES = new Set([
  'DAW_NOT_CONFIGURED',
  'DAW_UNKNOWN_USER',
  'DAW_PATIENT_INCOMPLETE',
  'MODULE_DISABLED',
]);

// Labels for the field keys DawPrescribingService reports in details.missing.
const MISSING_FIELD_LABELS: Record<string, string> = {
  firstName: 'First name',
  lastName: 'Last name',
  dob: 'Date of birth',
  gender: 'Gender',
  addressLine1: 'Street address',
  city: 'City',
  state: 'State',
  zip: 'ZIP code',
  // ScriptSure 400s when home, work and cell are all empty, so the session
  // endpoint reports a single `phone` failure rather than three field names.
  phone: 'Phone number',
};

export function missingFieldLabel(key: string): string {
  return MISSING_FIELD_LABELS[key] ?? key;
}

export interface ScriptSureErrorView {
  code: string | null;
  text: string;
  missing: string[];
  /** Null for the failures a retry cannot fix. */
  retry: (() => void) | null;
}

/** Known ahead of time from the prescriptions context payload; retrying can't fix it. */
export function notConfiguredView(): ScriptSureErrorView {
  return { code: 'DAW_NOT_CONFIGURED', text: NOT_CONFIGURED_TEXT, missing: [], retry: null };
}

export function errorViewFor(err: unknown, retry: () => void): ScriptSureErrorView {
  const code = getDawErrorCode(err);
  if (code === 'DAW_NOT_CONFIGURED') {
    return notConfiguredView();
  }
  if (code === 'DAW_UNKNOWN_USER') {
    return { code, text: UNKNOWN_USER_TEXT, missing: [], retry: null };
  }
  if (code === 'DAW_PATIENT_INCOMPLETE') {
    return { code, text: PATIENT_INCOMPLETE_TEXT, missing: getDawMissingFields(err), retry: null };
  }
  // DAW_BAD_SCOPE / DAW_CONSENT_PENDING / DAW_BUSY / DAW_UPSTREAM /
  // MODULE_DISABLED / anything else: show the server's message as a generic
  // alert, retryable unless the code is exempt.
  return {
    code,
    text: getErrorMessage(err, GENERIC_FAILURE_TEXT),
    missing: [],
    retry: code != null && RETRY_EXEMPT_CODES.has(code) ? null : retry,
  };
}

interface ScriptSureErrorNoticeProps {
  view: ScriptSureErrorView;
  /**
   * The patient whose details need fixing. The Prescribing page has none, so
   * it gets no Demographics link.
   */
  patientId?: number;
}

export function ScriptSureErrorNotice({ view, patientId }: ScriptSureErrorNoticeProps) {
  return (
    <div className="p-6 text-center text-sm text-rose-600">
      <strong>{view.text}</strong>
      {view.code === 'DAW_PATIENT_INCOMPLETE' && (
        <div className="mt-2 space-y-2 text-slate-700">
          <ul className="list-inside list-disc">
            {view.missing.map((field) => (
              <li key={field}>{missingFieldLabel(field)}</li>
            ))}
          </ul>
          {patientId != null && (
            <a
              href={`/patients/${patientId}?section=demographics`}
              className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
            >
              Complete them in Demographics
            </a>
          )}
        </div>
      )}
      {view.retry && (
        <div className="mt-3">
          <Button size="sm" variant="secondary" onClick={view.retry}>
            Try again
          </Button>
        </div>
      )}
    </div>
  );
}
